import "./crypto-engine.js";
import "./duress-panic-service.js";

// ── Cryptographically Secure Volatile Session Key Manager ──
function _generateCryptographicEntropy(byteLength = 32) {
    if (typeof window !== 'undefined' && window.crypto && window.crypto.getRandomValues) {
        const bytes = new Uint8Array(byteLength);
        window.crypto.getRandomValues(bytes);
        return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
    }
    return Math.random().toString(36).substring(2) + Date.now().toString(36);
}

(function purgeLegacyLocalStorage() {
    try {
        const forbiddenKeys = [
            '__cc_last_panic_ack',
            'cosmo_analytics',
            'cosmo_pdf_downloads_count',
            'cosmo_pdf_encrypted_downloads_count',
            'vault_usage',
            'current_suite_view',
            'cosmo_crypt_purchase_history',
            'cosmocrypt_sub_history',
            'card_mutation_config',
            'secure_pdf_config',
            'had_premium_plan',
            'last_notified_subscription',
            'premiumActive',
            'user_subscription',
            'cosmo_user_plan',
            'current_plan'
        ];
        forbiddenKeys.forEach(k => {
            try { localStorage.removeItem(k); } catch (e) { }
            try { if (window.parent && window.parent.localStorage) window.parent.localStorage.removeItem(k); } catch (e) { }
        });
        for (let i = localStorage.length - 1; i >= 0; i--) {
            const k = localStorage.key(i);
            if (k && (k.startsWith('_cosmo_phone_lock_') || k.startsWith('cosmo_lockout_') || forbiddenKeys.includes(k))) {
                localStorage.removeItem(k);
            }
        }
    } catch (e) { }
})();

(function initDeviceSalt() {
    try {
        localStorage.removeItem('cosmo_device_salt');
        localStorage.removeItem('__cc_sys_hash');
        localStorage.removeItem('userStoreEnc');
        localStorage.removeItem('userStore');
        localStorage.removeItem('card_mutation_config');
        localStorage.removeItem('secure_pdf_config');
        localStorage.removeItem('cosmocrypt_sub_enc');
        try { if (window.parent && window.parent.localStorage) {
            window.parent.localStorage.removeItem('card_mutation_config');
            window.parent.localStorage.removeItem('secure_pdf_config');
            window.parent.localStorage.removeItem('cosmocrypt_sub_enc');
        }} catch(e){}
        if (typeof window !== 'undefined') {
            try { delete window.__cc_volatile_key; } catch (e) { }
            try { delete window._getAppSecret; } catch (e) { }
        }
    } catch (e) { }
})();

let _volatileSessionKey = null;

function _getAppSecret() {
    try {
        if (_volatileSessionKey) return _volatileSessionKey;

        if (typeof sessionStorage !== 'undefined') {
            let sessionKey = sessionStorage.getItem('__cc_session_token');
            if (sessionKey) {
                _volatileSessionKey = sessionKey;
                return sessionKey;
            }
        }

        const newEntropy = _generateCryptographicEntropy(32);
        if (typeof sessionStorage !== 'undefined') {
            try { sessionStorage.setItem('__cc_session_token', newEntropy); } catch (e) { }
        }
        if (typeof localStorage !== 'undefined') {
            try {
                localStorage.removeItem('__cc_sys_hash');
            } catch (e) { }
        }
        _volatileSessionKey = newEntropy;
        return newEntropy;
    } catch (e) {
        return _generateCryptographicEntropy(32);
    }
}
const FORBIDDEN_SUBSCRIPTION_KEYS = [
    'user_subscription',
    'premiumActive',
    'had_premium_plan',
    'last_notified_subscription',
    'cosmo_user_plan',
    'current_plan'
];

const nativeGetItem = Storage.prototype.getItem;
const nativeSetItem = Storage.prototype.setItem;
const nativeRemoveItem = Storage.prototype.removeItem;

const originalSetItem = function (key, val) {
    if (FORBIDDEN_SUBSCRIPTION_KEYS.includes(key)) {
        return;
    }
    if (key === 'userStoreEnc' || key === 'userStore' || key === 'cosmocrypt_sub_enc') {
        try { sessionStorage.setItem(key, val); } catch (e) { }
        try { nativeRemoveItem.call(this, key); } catch (e) { }
        return;
    }
    return nativeSetItem.call(this, key, val);
};

const originalGetItem = function (key) {
    if (FORBIDDEN_SUBSCRIPTION_KEYS.includes(key)) {
        return Storage.prototype.getItem.call(this, key);
    }
    return nativeGetItem.call(this, key);
};

const originalRemoveItem = function (key) {
    return nativeRemoveItem.call(this, key);
};

if (typeof window !== 'undefined') {
    // window._getAppSecret removed: keep module-scoped to prevent global extraction
    window.originalGetItem = originalGetItem;
    window.originalSetItem = originalSetItem;
    window.originalRemoveItem = originalRemoveItem;
    window.FORBIDDEN_SUBSCRIPTION_KEYS = FORBIDDEN_SUBSCRIPTION_KEYS;
}

const MOBILE_SYNC_KEYS = [
    'active_layer_mode', 'suite_sound_muted', 'suite_sound_volume',
    'suite_typing_enabled', 'suite_typing_volume', 'suite_typing_tune',
    'suite_bottom_nav_enabled', 'suite_bottom_nav_volume',
    'nav_anim_enabled', 'session_protocol_enabled', 'fullscreen_on_click'
];

// ── Secure Cache Decryption & Signature Verification Helper ──
function getVerifiedPlanFromCache() {
    try {
        const TIER_TO_INTERNAL = { 'element': 'element', 'foundation': 'element', 'normal': 'element', 'catalyst': 'catalyst', 'velocity': 'catalyst', 'max': 'catalyst', 'matrix': 'matrix', 'pro': 'matrix', 'quantum': 'quantum', 'ultra': 'quantum' };

        if (nativeGetItem.call(localStorage, 'explicit_logout') === 'true') {
            return 'element';
        }

        const user = (window.suiteAuth && window.suiteAuth.currentUser) || (window.auth && window.auth.currentUser);
        // If there is NO active user session, ALWAYS return 'element'
        if (!user && !window.userData && !sessionStorage.getItem('userStoreEnc') && !nativeGetItem.call(localStorage, 'userStoreEnc')) {
            return 'element';
        }

        // Read encrypted cache from sessionStorage with fallback
        const enc = (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('cosmocrypt_sub_enc') : null) || nativeGetItem.call(localStorage, 'cosmocrypt_sub_enc');
        if (enc) {
            try {
                const b = CryptoJS.AES.decrypt(enc, _getAppSecret());
                const obj = JSON.parse(b.toString(CryptoJS.enc.Utf8));
                const uid = user ? user.uid : (window.userData ? window.userData.uid : null);

                if (uid && obj.uid === uid) {
                    const signPayload = uid + obj.plan + (obj.expiresAtMs || 0) + _getAppSecret();
                    const expectedSignature = CryptoJS.SHA256(signPayload).toString();
                    if (obj.signature === expectedSignature) {
                        const now = Date.now();
                        const exMs = obj.expiresAtMs || 0;
                        const isActive = (obj.plan === 'foundation' || obj.plan === 'normal' || obj.plan === 'element') ? true : (exMs > now && obj.status !== 'cancelled' && obj.status !== 'reset' && obj.status !== 'expired_downgrade');
                        if (isActive) {
                            return TIER_TO_INTERNAL[obj.plan] || 'element';
                        }
                    }
                }
            } catch (decErr) { }
        }

        return 'element';
    } catch (e) {
        return 'element';
    }
}

if (typeof window !== 'undefined') {
    window.getVerifiedPlanFromCache = getVerifiedPlanFromCache;
}

// ── Comprehensive Scope Storage Protection (applies to window and all iframes) ──
function installStorageSecurity(targetWin) {
    if (!targetWin) return;
    try {
        const proto = targetWin.Storage ? targetWin.Storage.prototype : null;
        const targetStorage = targetWin.localStorage;
        if (!proto || !targetStorage) return;

        // Immediately purge all forbidden subscription keys and sensitive storage from physical localStorage
        FORBIDDEN_SUBSCRIPTION_KEYS.forEach(k => {
            try { nativeRemoveItem.call(targetStorage, k); } catch (e) { }
        });
        try { nativeRemoveItem.call(targetStorage, 'userStoreEnc'); } catch (e) { }
        try { nativeRemoveItem.call(targetStorage, 'userStore'); } catch (e) { }

        function resolveVerifiedPlan() {
            try {
                const root = window.top || window.parent || window;
                const sub = root.CosmoCryptSubscription || root.cosmoSubscription || window.CosmoCryptSubscription || window.cosmoSubscription;
                if (sub && typeof sub.getActivePlan === 'function') {
                    return sub.getActivePlan();
                }
                if (typeof root.getVerifiedPlanFromCache === 'function') {
                    return root.getVerifiedPlanFromCache();
                }
                if (typeof getVerifiedPlanFromCache === 'function') {
                    return getVerifiedPlanFromCache();
                }
            } catch (e) { }
            return 'element';
        }

        function resolveIsPlanActive() {
            try {
                const root = window.top || window.parent || window;
                const sub = root.CosmoCryptSubscription || root.cosmoSubscription || window.CosmoCryptSubscription || window.cosmoSubscription;
                if (sub && typeof sub.isPlanActive === 'function') {
                    return sub.isPlanActive();
                }
            } catch (e) { }
            return resolveVerifiedPlan() !== 'element';
        }

        function resolveHadPremium() {
            try {
                const root = window.top || window.parent || window;
                const sub = root.CosmoCryptSubscription || root.cosmoSubscription || window.CosmoCryptSubscription || window.cosmoSubscription;
                if (sub && typeof sub.hadPremiumPlan === 'function') {
                    return sub.hadPremiumPlan();
                }
                if (sub && typeof sub.getPlanData === 'function') {
                    const d = sub.getPlanData();
                    if (d && (d.had_premium_plan === true || d.had_premium_plan === 'true')) return true;
                }
            } catch (e) { }
            return resolveVerifiedPlan() !== 'element';
        }

        // Intercept Storage.prototype.getItem
        Object.defineProperty(proto, 'getItem', {
            value: function (key) {
                try {
                    if (sessionStorage.getItem('__cc_duress_active') === 'true' || window.__cc_is_duress === true) {
                        const isVaultDataKey = key.includes('vault') || key.includes('credentials') || key.includes('node_infrastructure') || key.includes('developer_endpoints') || key.includes('workspace_integration') || key.includes('encrypted_partitions') || key.includes('payment_gateways') || key.includes('merchant_identities') || key.includes('national_identity') || key.includes('dns_management');
                        if (isVaultDataKey) {
                            return null;
                        }
                    }
                } catch (e) { }

                // LocalStorage must never expose userStoreEnc
                if (key === 'userStoreEnc' || key === 'userStore' || key === 'cosmocrypt_sub_enc') {
                    if (this === targetStorage) {
                        return null;
                    }
                    return nativeGetItem.call(this, key);
                }

                if (key === 'user_subscription' || key === 'cosmo_user_plan' || key === 'current_plan') {
                    return resolveVerifiedPlan();
                }
                if (key === 'premiumActive') {
                    const plan = resolveVerifiedPlan();
                    const isPrem = resolveIsPlanActive() && plan !== 'element' && plan !== 'normal' && plan !== 'foundation';
                    return isPrem ? 'true' : 'false';
                }
                if (key === 'had_premium_plan') {
                    return resolveHadPremium() ? 'true' : 'false';
                }
                if (key === 'last_notified_subscription') {
                    const root = window.top || window.parent || window;
                    return root.__last_notified_subscription || resolveVerifiedPlan() || 'element';
                }
                if (MOBILE_SYNC_KEYS.includes(key)) {
                    return nativeGetItem.call(this, 'mobile_' + key);
                }
                return nativeGetItem.call(this, key);
            },
            writable: false, configurable: false
        });

        // Intercept Storage.prototype.setItem to strictly block writes to forbidden keys
        Object.defineProperty(proto, 'setItem', {
            value: function (key, val) {
                if (this === targetStorage) {
                    // Redirect sensitive user stores strictly to ephemeral sessionStorage
                    if (key === 'userStoreEnc' || key === 'userStore' || key === 'cosmocrypt_sub_enc') {
                        try { sessionStorage.setItem(key, val); } catch (e) { }
                        try { nativeRemoveItem.call(targetStorage, key); } catch (e) { }
                        return;
                    }
                    if (key === '__cc_last_panic_ack' || (typeof key === 'string' && (key.startsWith('_cosmo_phone_lock_') || key.startsWith('cosmo_lockout_')))) {
                        try { sessionStorage.setItem(key, val); } catch (e) { }
                        return;
                    }
                    if (key === 'cosmo_analytics' || key === 'cosmo_pdf_downloads_count' || key === 'cosmo_pdf_encrypted_downloads_count' || key === 'vault_usage' || key === 'current_suite_view' || key === 'cosmo_crypt_purchase_history') {
                        return;
                    }
                }
                if (FORBIDDEN_SUBSCRIPTION_KEYS.includes(key)) {
                    // Strictly forbid writing subscription data to localStorage.
                    // All subscription state relies exclusively on Firebase and cannot be modified from console or scripts.
                    return;
                }
                if (MOBILE_SYNC_KEYS.includes(key)) {
                    return nativeSetItem.call(this, 'mobile_' + key, val);
                }
                return nativeSetItem.call(this, key, val);
            },
            writable: false, configurable: false
        });

        // Intercept Storage.prototype.removeItem
        Object.defineProperty(proto, 'removeItem', {
            value: function (key) {
                if (key === 'userStoreEnc' || key === 'userStore' || key === 'cosmocrypt_sub_enc') {
                    try { sessionStorage.removeItem(key); } catch (e) { }
                    return nativeRemoveItem.call(this, key);
                }
                if (MOBILE_SYNC_KEYS.includes(key)) {
                    return nativeRemoveItem.call(this, 'mobile_' + key);
                }
                return nativeRemoveItem.call(this, key);
            },
            writable: false, configurable: false
        });

        // Block direct property tampering (e.g. localStorage.user_subscription = 'quantum')
        FORBIDDEN_SUBSCRIPTION_KEYS.forEach(key => {
            try {
                Object.defineProperty(proto, key, {
                    get: function () { return this.getItem(key); },
                    set: function () { /* strictly immutable */ },
                    configurable: false,
                    enumerable: false
                });
            } catch (e) { }
            try {
                Object.defineProperty(targetStorage, key, {
                    get: function () { return targetStorage.getItem(key); },
                    set: function () { /* strictly immutable */ },
                    configurable: false,
                    enumerable: false
                });
            } catch (e) { }
        });
    } catch (e) { }
}

if (typeof window !== 'undefined') {
    window.installStorageSecurity = installStorageSecurity;
    installStorageSecurity(window);
}

/* --- SAFE LOCALSTORAGE WRAPPER (fixes Chrome file:// crash) --- */
window.safeStorage = {
    getItem: function (key) { try { return localStorage.getItem(key); } catch (e) { return null; } },
    setItem: function (key, val, isRemote = false) {
        try {
            localStorage.setItem(key, val);
            window.dispatchEvent(new CustomEvent('safeStorageChange', { detail: { key, val, isRemote } }));
            if (window.parent && window.parent !== window) {
                try { window.parent.dispatchEvent(new CustomEvent('safeStorageChange', { detail: { key, val, isRemote } })); } catch (err) { }
            }
            document.querySelectorAll('iframe').forEach(iframe => {
                try {
                    if (iframe.contentWindow) {
                        iframe.contentWindow.dispatchEvent(new CustomEvent('safeStorageChange', { detail: { key, val, isRemote } }));
                    }
                } catch (err) { }
            });
        } catch (e) { }
    },
    removeItem: function (key) {
        try {
            localStorage.removeItem(key);
            window.dispatchEvent(new CustomEvent('safeStorageChange', { detail: { key, val: null } }));
            if (window.parent && window.parent !== window) {
                try { window.parent.dispatchEvent(new CustomEvent('safeStorageChange', { detail: { key, val: null } })); } catch (err) { }
            }
            document.querySelectorAll('iframe').forEach(iframe => {
                try {
                    if (iframe.contentWindow) {
                        iframe.contentWindow.dispatchEvent(new CustomEvent('safeStorageChange', { detail: { key, val: null } }));
                    }
                } catch (err) { }
            });
        } catch (e) { }
    },
    clear: function () {
        try {
            localStorage.clear();
            window.dispatchEvent(new CustomEvent('safeStorageChange', { detail: { clear: true } }));
            if (window.parent && window.parent !== window) {
                try { window.parent.dispatchEvent(new CustomEvent('safeStorageChange', { detail: { clear: true } })); } catch (err) { }
            }
            document.querySelectorAll('iframe').forEach(iframe => {
                try {
                    if (iframe.contentWindow) {
                        iframe.contentWindow.dispatchEvent(new CustomEvent('safeStorageChange', { detail: { clear: true } }));
                    }
                } catch (err) { }
            });
        } catch (e) { }
    }
};
const safeStorage = window.safeStorage;

// 📊 GLOBAL ANALYTICS BRIDGE [RESILIENT & LOCAL-ONLY]
window.cosmoCryptAnalytics = window.cosmoAnalytics = {
    ready: true,
    logNotification: (title, message, type) => {
    },
    logScreenView: (screen_name) => {
    },
    logAuthAction: (action, method) => {
    },
    logSettingsUpdate: (setting_count) => {
    },
    logFavoriteSync: (action, item_id) => {
    },
    logLinkAction: (action_type, target_url, status = 'attempt') => {
    },
    logError: (message, context) => {
    }
};

// --- Parent Database & Plan limits Single Source of Truth ---
window.planFullData = {
    'element': {
        name: "ELEMENT",
        deviceLimit: 1,
        deviceDescription: "Single-Device Access Model",
        features: ["1 Device Login"],
        hasSSS: false,
        hasPanic: false
    },
    'catalyst': {
        name: "CATALYST",
        deviceLimit: 2,
        deviceDescription: "Dual-Device Synced Vault Access",
        features: ["2 Devices Login"],
        hasSSS: false,
        hasPanic: false
    },
    'matrix': {
        name: "MATRIX",
        deviceLimit: 3,
        deviceDescription: "Multi-Device Multi-Node Vault Sync",
        features: ["3 Devices Login", "Shamir's Secret Sharing (SSS)", "Emergency Panic Wipe"],
        hasSSS: true,
        hasPanic: true
    },
    'quantum': {
        name: "QUANTUM",
        deviceLimit: 5,
        deviceDescription: "Enterprise-Grade 5 Device Synchronized Logins",
        features: ["5 Device Logins", "Shamir's Secret Sharing (SSS)", "Emergency Panic Shredder"],
        hasSSS: true,
        hasPanic: true
    },
    'normal': { name: "ELEMENT", deviceLimit: 1, deviceDescription: "Single-Device Access Model", features: ["1 Device Login"], hasSSS: false, hasPanic: false },
    'foundation': { name: "ELEMENT", deviceLimit: 1, deviceDescription: "Single-Device Access Model", features: ["1 Device Login"], hasSSS: false, hasPanic: false },
    'velocity': { name: "CATALYST", deviceLimit: 2, deviceDescription: "Dual-Device Synced Vault Access", features: ["2 Devices Login"], hasSSS: false, hasPanic: false },
    'max': { name: "CATALYST", deviceLimit: 2, deviceDescription: "Dual-Device Synced Vault Access", features: ["2 Devices Login"], hasSSS: false, hasPanic: false },
    'pro': { name: "MATRIX", deviceLimit: 3, deviceDescription: "Multi-Device Multi-Node Vault Sync", features: ["3 Devices Login", "Shamir's Secret Sharing (SSS)", "Emergency Panic Wipe"], hasSSS: true, hasPanic: true },
    'ultra': { name: "QUANTUM", deviceLimit: 5, deviceDescription: "Enterprise-Grade 5 Device Synchronized Logins", features: ["5 Device Logins", "Shamir's Secret Sharing (SSS)", "Emergency Panic Shredder"], hasSSS: true, hasPanic: true }
};

window.getCurrentStorageLimits = function () {
    let sub = localStorage.getItem('user_subscription') || 'element';

    // 🔐 Secure check: Override local storage if Firebase auth is initialized and active
    if (window.cosmoSubscription && window.suiteAuth && window.suiteAuth.currentUser) {
        const TIER_TO_INTERNAL = { 'element': 'element', 'foundation': 'element', 'normal': 'element', 'catalyst': 'catalyst', 'velocity': 'catalyst', 'max': 'catalyst', 'matrix': 'matrix', 'pro': 'matrix', 'quantum': 'quantum', 'ultra': 'quantum' };
        const cached = window.cosmoSubscription.getPlanData();
        if (cached && cached.plan) {
            sub = TIER_TO_INTERNAL[cached.plan] || cached.plan;
        }
    }

    const plan = window.planFullData[sub] || window.planFullData['element'];
    const getLimit = (pattern, fallback) => {
        const feature = plan.features.find(f => f.includes(pattern));
        if (!feature) return parseInt(fallback);
        const match = feature.match(/\d+/);
        return match ? parseInt(match[0]) : parseInt(fallback);
    };
    return {
        creator: getLimit("Encrypted Slots", "15"),
        history: getLimit("Key History Logs", "15")
    };
};

window.trimAllHistoriesToLimits = async function () {
    try {
        if (typeof window.syncUsedSlotsToFirebase === 'function') window.syncUsedSlotsToFirebase();
    } catch (err) {
        console.error("Error trimming history to limits:", err);
    }
};

window.syncParentSubscription = function () {
    try {
        const user = (window.suiteAuth && window.suiteAuth.currentUser) || (window.auth && window.auth.currentUser);
        const isLoggedIn = !!(user || (window.userData && window.userData.uid) || sessionStorage.getItem('userStoreEnc') || nativeGetItem.call(localStorage, 'userStoreEnc'));
        const isExplicitLogout = nativeGetItem.call(localStorage, 'explicit_logout') === 'true';

        if (!isLoggedIn || isExplicitLogout) {
            window.__last_notified_subscription = 'element';
            try {
                nativeRemoveItem.call(localStorage, 'user_subscription');
                nativeRemoveItem.call(localStorage, 'last_notified_subscription');
                nativeRemoveItem.call(localStorage, 'premiumActive');
                nativeRemoveItem.call(localStorage, 'had_premium_plan');
            } catch (e) { }
            return;
        }

        // 🔐 Firebase-first: If cosmoSubscription is initialized and user logged in, it is authority
        if (window.cosmoSubscription && user) {
            const TIER_TO_INTERNAL = { 'element': 'element', 'foundation': 'element', 'normal': 'element', 'catalyst': 'catalyst', 'velocity': 'catalyst', 'max': 'catalyst', 'matrix': 'matrix', 'pro': 'matrix', 'quantum': 'quantum', 'ultra': 'quantum' };
            const cached = window.cosmoSubscription.getPlanData();
            if (cached && cached.plan) {
                const sub = TIER_TO_INTERNAL[cached.plan] || cached.plan;
                let oldSub = window.__last_notified_subscription;
                if (!oldSub) {
                    oldSub = sub;
                    window.__last_notified_subscription = oldSub;
                }
                if (oldSub !== sub) {
                    const TIER_RANK = { 'element': 0, 'foundation': 0, 'normal': 0, 'catalyst': 1, 'velocity': 1, 'max': 1, 'matrix': 2, 'pro': 2, 'quantum': 3, 'ultra': 3 };
                    const oldLevel = TIER_RANK[oldSub] !== undefined ? TIER_RANK[oldSub] : -1;
                    const newLevel = TIER_RANK[sub] !== undefined ? TIER_RANK[sub] : -1;

                    if (oldSub && oldLevel !== -1 && newLevel !== -1) {
                        if (newLevel > oldLevel) {
                            const newPlanName = (window.planFullData[sub] || { name: sub.toUpperCase() }).name;
                            if (typeof window.addSuiteNotification === 'function') {
                                window.addSuiteNotification('account', 'Subscription Upgraded', `Your plan has been upgraded to ${newPlanName}. Enjoy higher limits and premium features!`, { 'New Plan': newPlanName, 'Status': 'Active' });
                            }
                            // Only fire celebration when plan genuinely upgrades
                            try { nativeSetItem.call(localStorage, 'pending_celebration', 'true'); } catch (e) { }
                        } else if (newLevel < oldLevel) {
                            const newPlanName = (window.planFullData[sub] || { name: sub.toUpperCase() }).name;
                            if (typeof window.addSuiteNotification === 'function') {
                                window.addSuiteNotification('account', 'Subscription Downgraded', `Your plan has been downgraded to ${newPlanName}.`, { 'New Plan': newPlanName, 'Status': 'Active' });
                            }
                            if (typeof window.trimAllHistoriesToLimits === 'function') window.trimAllHistoriesToLimits();
                        }
                    }

                    window.__last_notified_subscription = sub;
                    window.dispatchEvent(new CustomEvent('safeStorageChange', { detail: { key: 'user_subscription', val: sub, newValue: sub } }));
                    document.querySelectorAll('iframe').forEach(iframe => {
                        try { if (iframe.contentWindow) iframe.contentWindow.dispatchEvent(new CustomEvent('safeStorageChange', { detail: { key: 'user_subscription', val: sub, newValue: sub } })); } catch (err) { }
                    });
                }
                return;
            }
        }
        // Fallback: cosmoSubscription/auth not ready yet — try encrypted cache (cold-start)
        const coldSub = getVerifiedPlanFromCache();
        const sub = coldSub || 'element';
        let oldSub = window.__last_notified_subscription;
        if (!oldSub) {
            oldSub = sub;
            window.__last_notified_subscription = oldSub;
        }
        if (oldSub !== sub) {
            const TIER_RANK = { 'element': 0, 'foundation': 0, 'normal': 0, 'catalyst': 1, 'velocity': 1, 'max': 1, 'matrix': 2, 'pro': 2, 'quantum': 3, 'ultra': 3 };
            const oldLevel = TIER_RANK[oldSub] !== undefined ? TIER_RANK[oldSub] : -1;
            const newLevel = TIER_RANK[sub] !== undefined ? TIER_RANK[sub] : -1;

            window.__last_notified_subscription = sub;
            window.dispatchEvent(new CustomEvent('safeStorageChange', { detail: { key: 'user_subscription', val: sub, newValue: sub } }));
            document.querySelectorAll('iframe').forEach(iframe => {
                try { if (iframe.contentWindow) iframe.contentWindow.dispatchEvent(new CustomEvent('safeStorageChange', { detail: { key: 'user_subscription', val: sub, newValue: sub } })); } catch (err) { }
            });

            if (newLevel !== -1 && oldLevel !== -1 && newLevel < oldLevel) {
                if (typeof window.trimAllHistoriesToLimits === 'function') window.trimAllHistoriesToLimits();
            }
        }
    } catch (e) {
        console.error("Error in syncParentSubscription:", e);
    }
};

// Run sync on startup in the parent window
window.syncParentSubscription();

// Listen for storage events in the parent window
window.addEventListener('storage', (e) => {
    if (e.key === 'cosmo_crypt_purchase_history' || e.key === 'cosmo_crypt_purchase_history' || e.key === 'premiumActive' || e.key === 'user_subscription') {
        window.syncParentSubscription();
    }
});
window.addEventListener('safeStorageChange', (e) => {
    if (e.detail && (e.detail.key === 'cosmo_crypt_purchase_history' || e.detail.key === 'cosmo_crypt_purchase_history' || e.detail.key === 'premiumActive' || e.detail.key === 'user_subscription')) {
        window.syncParentSubscription();
    }
});

window.addSuiteNotification = function (type, title, message, detail = {}) {
    let notifs = [];
    try {
        const raw = localStorage.getItem('cosmo_crypt_notifications') || localStorage.getItem('cosmo_crypt_notifications');
        if (raw) {
            notifs = JSON.parse(raw);
        } else {
            notifs = [];
        }
    } catch (e) {
        notifs = [];
    }

    // Prevent exact identical notifications within 5 seconds to avoid double-logging/spamming
    const isDuplicate = notifs.some(n =>
        n.title === title &&
        n.message === message &&
        (Date.now() - (n.timestamp || 0) < 5000)
    );
    if (isDuplicate) return;

    const iconMap = {
        create: 'add_box',
        account: 'person',
        system: 'info'
    };

    const newNotif = {
        id: 'n_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5),
        type: type,
        icon: iconMap[type] || 'info',
        title: title,
        message: message,
        time: 'Just now',
        timeGroup: 'today',
        timestamp: Date.now(),
        read: false,
        detail: detail
    };

    notifs.unshift(newNotif);
    safeStorage.setItem('cosmo_crypt_notifications', JSON.stringify(notifs));
};

window.checkStorageNotifications = async function () {
    try {
        const limits = window.getCurrentStorageLimits();
    } catch (err) {
        console.error("Error checking storage limits:", err);
    }
};

// Bridge: Global Notification Proxy for internal scripts
window.pop = (msg, type = 'info') => {
    if (typeof window.showNotification === 'function') {
        window.showNotification('CLOUD SECURITY', msg, type);
    } else if (typeof window.showToast === 'function') {
        window.showToast(msg, type === 'error');
    } else {
        // Deferred retry: app-controller.js may not have loaded yet (fast Firebase sync)
        const retryInterval = setInterval(() => {
            if (typeof window.showToast === 'function') {
                clearInterval(retryInterval);
                window.showToast(msg, type === 'error');
            } else if (typeof window.showNotification === 'function') {
                clearInterval(retryInterval);
                window.showNotification('CLOUD SECURITY', msg, type);
            }
        }, 200);
        // Give up after 5 seconds to avoid leaking the interval
        setTimeout(() => {
            clearInterval(retryInterval);
        }, 5000);
    }
};

// ── Global Emergency Panic Hotkey (Ctrl + Shift + Alt + P) ──
if (typeof window !== 'undefined') {
    window.addEventListener('keydown', (e) => {
        if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.altKey && (e.key === 'P' || e.key === 'p' || e.code === 'KeyP')) {
            e.preventDefault();
            e.stopPropagation();

            const sub = (localStorage.getItem('user_subscription') || localStorage.getItem('cosmo_user_plan') || localStorage.getItem('current_plan') || '').toLowerCase();
            const isMatrixOrQuantum = sub.includes('matrix') || sub.includes('quantum') || sub.includes('pro') || sub.includes('ultra') || sub.includes('infinite') || sub.includes('lifetime');
            if (!isMatrixOrQuantum) {
                if (typeof window.showToast === 'function') {
                    window.showToast("Emergency Panic Wipe is an exclusive Matrix & Quantum feature. Please upgrade your plan.", "warning");
                }
                return;
            }

            if (window.CosmoDuress && typeof window.CosmoDuress.executePanicWipe === 'function') {
                window.CosmoDuress.executePanicWipe();
            } else if (typeof window.executePanicWipe === 'function') {
                window.executePanicWipe();
            } else if (typeof window.triggerPanicWipe === 'function') {
                window.triggerPanicWipe();
            } else if (window.parent && window.parent.CosmoDuress && typeof window.parent.CosmoDuress.executePanicWipe === 'function') {
                window.parent.CosmoDuress.executePanicWipe();
            } else if (window.parent && typeof window.parent.executePanicWipe === 'function') {
                window.parent.executePanicWipe();
            } else if (window.parent && typeof window.parent.triggerPanicWipe === 'function') {
                window.parent.triggerPanicWipe();
            }
        }
    }, true);
}
